import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCategoryParam } from '../../hooks/useCategoryParam';
import { useAuth } from '../../contexts/AuthContext';
import AddressSelector from '../../components/customer/AddressSelector';
import { useCart } from '../../contexts/CartContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Search, Plus, Minus, MapPin, ChevronDown, Clock, User,
  Home, Grid3X3, ShoppingCart, Sparkles, ChevronRight,
  Percent, Package, CalendarCheck, Bell
} from 'lucide-react';
import axios from 'axios';
import { PromoBannerCarousel } from '../../components/customer/PromoBannerCarousel';
import { resolveHomeBanner, themeHomeBannerDefaults } from '../../utils/homeBanner';
import { CouponStrip } from '../../components/customer/CouponStrip';
import { PromotionStrip } from '../../components/customer/PromotionStrip';
import { usePromotions } from '../../hooks/usePromotions';
import { UnavailableBadge, isUnavailable, isVariantUnavailable } from '../../components/customer/UnavailableBadge';
import { ComingSoonButton } from '../../components/customer/ComingSoonButton';
import { isComingSoon } from '../../utils/comingSoon';
import { ProductImageCarousel } from '../../components/customer/ProductImageCarousel';
import { SubCategoryFilter } from '../../components/customer/SubCategoryFilter';
import { VariantPicker } from '../../components/customer/VariantPicker';
import { CustomerDesktopNav } from '../../components/customer/CustomerDesktopNav';
import { CATEGORY_ICON_GRID, CUSTOMER_PAGE, PRODUCT_GRID } from '../../components/customer/customerLayout';
import { isNavItemActive, useCustomerNavItems } from '../../components/customer/customerNav';
import { ScrollRow } from '../../components/customer/ScrollRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEARCH_PLACEHOLDERS = [
  'Search for "milk"',
  'Search for "bread"',
  'Search for "eggs"',
  'Search for "rice"',
  'Search for "fruits"',
  'Search for "snacks"',
];

const RotatingPlaceholder = () => {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx(prev => (prev + 1) % SEARCH_PLACEHOLDERS.length);
        setFade(true);
      }, 200);
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  return (
    <span
      className="text-gray-400 text-sm transition-all duration-200 pointer-events-none"
      style={{ opacity: fade ? 1 : 0, transform: fade ? 'translateY(0)' : 'translateY(-4px)' }}
    >
      {SEARCH_PLACEHOLDERS[idx]}
    </span>
  );
};

const InstamartHome = () => {
  const navigate = useNavigate();
  const { user, storeSettings } = useAuth();
  const { addItem, getItemQuantity, updateQuantity, totalItems, totalAmount } = useCart();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  // In the URL, not local state, so Back from a product returns to this tab.
  const [selectedTab, setSelectedTab] = useCategoryParam();
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const autoPlay = storeSettings?.auto_image_carousel !== false;
  const detailEnabled = storeSettings?.product_detail_page !== false;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [promoBanners, setPromoBanners] = useState([]);
  const heroBanner = resolveHomeBanner(storeSettings?.home_banner, themeHomeBannerDefaults('theme-instamart'));
  const [coupons, setCoupons] = useState([]);
  const promotions = usePromotions();
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  // Serviceable Apartments: once the tenant uses the apartment-first address
  // flow (feature on + at least one apartment configured — same gating as
  // MapAddressPicker/DeliveryOrders/AdminOrders), delivery is always to one
  // known apartment/flat rather than a choice between several saved
  // addresses, so the generic multi-address switcher doesn't apply here.
  const isApartmentsEnabled = storeSettings?.tenant_features?.serviceable_apartments === true;
  const [hasApartments, setHasApartments] = useState(false);
  useEffect(() => {
    if (!isApartmentsEnabled) return;
    axios.get(`${API}/admin/serviceable-apartments`)
      .then(res => setHasApartments((res.data || []).length > 0))
      .catch(() => {});
  }, [isApartmentsEnabled]);
  const apartmentFlowActive = isApartmentsEnabled && hasApartments;
  const searchRef = useRef(null);

  const sessionId = useRef(
    sessionStorage.getItem('_sid') ||
    (() => { const s = Math.random().toString(36).slice(2); sessionStorage.setItem('_sid', s); return s; })()
  );

  useEffect(() => { loadData(); trackPageview(); }, [storeSettings?.admin_id]);

  const loadData = async () => {
    try {
      const adminId = user?.admin_id || storeSettings?.admin_id;
      let reqs;
      if (user && user.admin_id) {
        // Authenticated — use normal APIs
        reqs = [
          axios.get(`${API}/admin/categories`),
          axios.get(`${API}/customer/products`),
          axios.get(`${API}/store/${user.admin_id}/promo-banners`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${user.admin_id}/coupons`).catch(() => ({ data: [] })),
        ];
      } else if (adminId) {
        // Guest browsing — use public APIs
        reqs = [
          axios.get(`${API}/public/store/${adminId}/categories`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/store/${adminId}/products`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${adminId}/promo-banners`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${adminId}/coupons`).catch(() => ({ data: [] })),
        ];
      } else {
        setLoading(false);
        return;
      }
      const results = await Promise.all(reqs);
      setCategories(results[0].data);
      setProducts(results[1].data);
      if (results[2]) setPromoBanners(results[2].data);
      if (results[3]) setCoupons(results[3].data);
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  const trackPageview = () => {
    if (!user?.admin_id) return;
    axios.post(`${API}/track/pageview`, {
      admin_id: user.admin_id, customer_id: user.id,
      session_id: sessionId.current, page: '/home', source: 'direct',
    }).catch(() => {});
  };

  const trackEvent = (eventType, extra = {}) => {
    if (!user?.admin_id) return;
    axios.post(`${API}/track/event`, {
      admin_id: user.admin_id, customer_id: user.id,
      session_id: sessionId.current, event_type: eventType, ...extra,
    }).catch(() => {});
  };

  const handleSelectTab = (tabId) => {
    setSelectedTab(tabId);
    setSelectedSubCategories([]);
  };

  const filteredProducts = (selectedTab
    ? products.filter(p => {
        if (p.category_id !== selectedTab) return false;
        if (selectedSubCategories.length > 0 && !selectedSubCategories.includes(p.sub_category_id)) return false;
        return true;
      })
    : products
  ).sort((a, b) => {
    const aOos = isUnavailable(a, storeSettings) ? 1 : 0;
    const bOos = isUnavailable(b, storeSettings) ? 1 : 0;
    return aOos - bOos;
  });

  const searchedProducts = searchQuery.trim()
    ? products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : null;

  const handleAddToCart = (product, selectedVariant = null) => {
    if (!user) {
      navigate('/login');
      toast.info('Please login to add items to cart');
      return;
    }
    addItem(product, 1, selectedVariant);
    const label = selectedVariant ? `${product.name} (${selectedVariant.label})` : product.name;
    toast.success(`Added ${label}`);
    trackEvent('add_to_cart', { product_id: product.id, product_name: product.name, variant: selectedVariant?.label });
  };

  const currencySymbol = storeSettings?.currency_symbol || '₹';

  // Separate product lists
  const discountedProducts = products.filter(p => p.mrp > p.price).slice(0, 12);
  const popularProducts = products.slice(0, 12);

  const displayProducts = searchedProducts || filteredProducts;

  return (
    <div className="min-h-screen bg-[var(--theme-background)] pb-24 lg:pb-10" data-testid="instamart-home">
      {/* ======= HEADER ======= */}
      <header className="bg-[var(--theme-surface)] sticky top-0 z-50 shadow-sm" data-testid="instamart-header">
        <div className={`${CUSTOMER_PAGE} px-4 py-3 flex items-center justify-between gap-4`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-[var(--theme-primary)]" />
              <span className="font-heading font-bold text-lg text-[var(--theme-primary)]">
                {storeSettings?.store_name || '8 mins'}
              </span>
            </div>
            {!apartmentFlowActive && (
              <div className="mt-0.5 max-w-full">
                <AddressSelector
                  selectedAddress={selectedDeliveryAddress}
                  onSelect={setSelectedDeliveryAddress}
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <CustomerDesktopNav />
            <Link to="/notifications" className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-testid="header-notification-btn">
              <Bell className="w-5 h-5 text-[var(--theme-text-main)]" />
            </Link>
            <Link to="/cart" className="lg:hidden relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-testid="header-cart-btn">
              <ShoppingCart className="w-5 h-5 text-[var(--theme-text-main)]" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[var(--theme-primary)] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {totalItems}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      <div className={CUSTOMER_PAGE}>
        {/* ======= SEARCH BAR ======= */}
        <div className="px-4 pt-3 pb-2" data-testid="search-section">
          <div
            className={`bg-[var(--theme-surface)] border rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm transition-all ${searchFocused ? 'border-[var(--theme-primary)] ring-2 ring-[var(--theme-primary)]/10' : 'border-[var(--theme-border)]'}`}
            onClick={() => searchRef.current?.focus()}
          >
            <Search className="w-5 h-5 text-gray-400 shrink-0" />
            <div className="flex-1 relative">
              {/* Input stays in flow so it defines this wrapper's height — see ZeptoHome. */}
              {!searchFocused && !searchQuery && (
                <div className="absolute inset-0 flex items-center pointer-events-none">
                  <RotatingPlaceholder />
                </div>
              )}
              <input
                ref={searchRef}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                className="w-full bg-transparent outline-none text-sm text-[var(--theme-text-main)]"
                data-testid="instamart-search-input"
              />
            </div>
          </div>
        </div>

        {/* ======= SEARCH RESULTS ======= */}
        {searchedProducts && (
          <div className="px-4 pb-4" data-testid="search-results">
            <p className="text-xs text-[var(--theme-text-muted)] mb-2">
              {searchedProducts.length} result{searchedProducts.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
            <div className={PRODUCT_GRID}>
              {searchedProducts.slice(0, 8).map(product => (
                <ProductCardGrid key={product.id} product={product} currencySymbol={currencySymbol}
                  getItemQuantity={getItemQuantity} onAdd={(v) => handleAddToCart(product, v)} onAddBase={() => handleAddToCart(product)}
                  onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings}
                  autoPlay={autoPlay} detailEnabled={detailEnabled} navigate={navigate} />
              ))}
            </div>
            {searchedProducts.length === 0 && (
              <p className="text-center py-8 text-[var(--theme-text-muted)] text-sm">No products found</p>
            )}
          </div>
        )}

        {/* ======= MAIN CONTENT (hide when searching) ======= */}
        {!searchedProducts && (
          <>
            {/* Category Tabs */}
            <div className="flex gap-2.5 overflow-x-auto scrollbar-hide px-4 pb-3 pt-1" data-testid="category-tabs">
              <button
                onClick={() => handleSelectTab(null)}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full border text-sm font-medium transition-colors shrink-0 ${!selectedTab ? 'bg-[var(--theme-secondary)] text-[var(--theme-primary)] border-[var(--theme-primary)]' : 'bg-[var(--theme-surface)] text-[var(--theme-text-muted)] border-[var(--theme-border)] hover:bg-[var(--theme-secondary)]'}`}
                data-testid="tab-all"
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleSelectTab(cat.id)}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full border text-sm font-medium transition-colors shrink-0 ${selectedTab === cat.id ? 'bg-[var(--theme-secondary)] text-[var(--theme-primary)] border-[var(--theme-primary)]' : 'bg-[var(--theme-surface)] text-[var(--theme-text-muted)] border-[var(--theme-border)] hover:bg-[var(--theme-secondary)]'}`}
                  data-testid={`tab-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* PROMO BANNER - Lowest Prices Everyday (admin: Settings → Home Page Banner) */}
            {heroBanner && (
            <div className="px-4 mb-5" data-testid="promo-banner">
              <div className="rounded-2xl overflow-hidden relative bg-gradient-to-br from-[#0C831F] via-[#1a9c34] to-[#2ba643] p-5 text-white shadow-md" style={{ minHeight: '130px' }}>
                <div className="relative z-10 max-w-[60%]">
                  {heroBanner.customTitle ? (
                    <h2 className="font-heading font-extrabold text-2xl leading-tight tracking-tight break-words">{heroBanner.title}</h2>
                  ) : (
                    <>
                      <h2 className="font-heading font-extrabold text-2xl leading-tight tracking-tight">Lowest Prices</h2>
                      <h2 className="font-heading font-extrabold text-2xl leading-tight tracking-tight">Everyday</h2>
                    </>
                  )}
                  {heroBanner.subtitle && <p className="text-xs mt-2 opacity-80 break-words">{heroBanner.subtitle}</p>}
                  {heroBanner.badge && (
                    <span className="inline-block mt-2 bg-white/20 px-3 py-1 rounded-full text-xs font-semibold">{heroBanner.badge}</span>
                  )}
                </div>
                {/* Decorative elements */}
                <div className="absolute right-3 top-2 bottom-2 w-28 opacity-20 flex items-center justify-center">
                  <svg viewBox="0 0 120 120" className="w-full h-full">
                    <circle cx="60" cy="60" r="50" fill="rgba(255,255,255,0.2)" />
                    <circle cx="85" cy="35" r="25" fill="rgba(255,255,255,0.15)" />
                    <circle cx="35" cy="85" r="20" fill="rgba(255,255,255,0.1)" />
                    <path d="M40 50 Q60 20 80 50 Q100 80 60 90 Q20 80 40 50Z" fill="rgba(255,255,255,0.15)" />
                  </svg>
                </div>
                {/* Sparkle accent */}
                <div className="absolute top-3 right-4">
                  <Percent className="w-5 h-5 text-white/40" />
                </div>
              </div>
            </div>
            )}

            {/* Admin Promo Banners */}
            <PromoBannerCarousel banners={promoBanners} variant="instamart" />

            {/* Coupons */}
            {/* Promotions (automatic discounts) */}
            <PromotionStrip promotions={promotions} variant="instamart" />

            <CouponStrip coupons={coupons} variant="instamart" currencySymbol={currencySymbol} />

            {/* ======= WHEN CATEGORY SELECTED: show grid ======= */}
            {selectedTab ? (
              <div className="px-4 pb-6" data-testid="category-products">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)]">
                    {categories.find(c => c.id === selectedTab)?.name || 'Products'}
                  </h3>
                  <span className="text-xs text-[var(--theme-text-muted)]">{filteredProducts.length} items</span>
                </div>
                <SubCategoryFilter
                  categories={categories}
                  selectedTab={selectedTab}
                  selectedSubCategories={selectedSubCategories}
                  onSelect={setSelectedSubCategories}
                />
                {loading ? (
                  <div className={PRODUCT_GRID}>
                    {[1,2,3,4].map(i => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />)}
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <p className="text-center py-8 text-[var(--theme-text-muted)] text-sm">No products in this category</p>
                ) : (
                  <div className={PRODUCT_GRID}>
                    {filteredProducts.map(product => (
                      <ProductCardGrid key={product.id} product={product} currencySymbol={currencySymbol}
                        onAdd={(v) => handleAddToCart(product, v)} onAddBase={() => handleAddToCart(product)}
                        onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} getItemQuantity={getItemQuantity}
                        storeSettings={storeSettings} autoPlay={autoPlay} detailEnabled={detailEnabled} navigate={navigate} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* ===== LOWEST PRICES - Horizontal Carousel ===== */}
                {discountedProducts.length > 0 && (
                  <div className="mb-6" data-testid="lowest-prices-section">
                    <div className="flex items-center justify-between px-4 mb-3">
                      <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)]">
                        Lowest Prices
                      </h3>
                      <Link to="/products" className="text-xs text-[var(--theme-primary)] font-medium flex items-center gap-0.5">
                        See all <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                    <ScrollRow className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-5 snap-x scroll-px-4" testId="lowest-prices-scroll">
                      {discountedProducts.map(product => (
                        <ProductCardCarousel key={product.id} product={product} currencySymbol={currencySymbol}
                          onAdd={(v) => handleAddToCart(product, v)} onAddBase={() => handleAddToCart(product)}
                          onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} getItemQuantity={getItemQuantity}
                          storeSettings={storeSettings} autoPlay={autoPlay} detailEnabled={detailEnabled} navigate={navigate} />
                      ))}
                    </ScrollRow>
                  </div>
                )}

                {/* ===== SHOP BY CATEGORY - 4 col grid ===== */}
                {categories.length > 0 && (
                  <div className="px-4 mb-6" data-testid="shop-by-category">
                    <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)] mb-4">
                      Shop by Category
                    </h3>
                    <div className={CATEGORY_ICON_GRID}>
                      {categories.slice(0, 8).map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => handleSelectTab(cat.id)}
                          className="instamart-cat-grid-item"
                          data-testid={`catgrid-${cat.id}`}
                        >
                          <div className="cat-img">
                            {cat.image_url ? (
                              <img src={cat.image_url} alt={cat.name} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[var(--theme-secondary)] to-[#d4edda]">
                                <span className="text-xl font-bold text-[var(--theme-primary)]">{cat.name.charAt(0)}</span>
                              </div>
                            )}
                          </div>
                          <span className="text-[11px] font-medium text-[var(--theme-text-main)] leading-tight line-clamp-2">
                            {cat.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== POPULAR / BESTSELLERS - Horizontal Carousel ===== */}
                {popularProducts.length > 0 && (
                  <div className="mb-6" data-testid="bestsellers-section">
                    <div className="flex items-center justify-between px-4 mb-3">
                      <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)]">
                        Top Deals on Bestsellers
                      </h3>
                      <Link to="/products" className="text-xs text-[var(--theme-primary)] font-medium flex items-center gap-0.5">
                        See all <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                    <ScrollRow className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-5 snap-x scroll-px-4" testId="bestsellers-scroll">
                      {popularProducts.map(product => (
                        <ProductCardCarousel key={product.id} product={product} currencySymbol={currencySymbol}
                          onAdd={(v) => handleAddToCart(product, v)} onAddBase={() => handleAddToCart(product)}
                          onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} getItemQuantity={getItemQuantity}
                          storeSettings={storeSettings} autoPlay={autoPlay} detailEnabled={detailEnabled} navigate={navigate} />
                      ))}
                    </ScrollRow>
                  </div>
                )}

                {/* ===== FREE DELIVERY BANNER ===== */}
                <div className="px-4 mb-6" data-testid="free-delivery-banner">
                  <div className="bg-gradient-to-r from-cyan-50 to-teal-50 border border-cyan-200 rounded-xl px-4 py-3 text-center">
                    <p className="text-sm text-cyan-900">
                      <strong>FREE DELIVERY</strong> on orders above {currencySymbol}199
                    </p>
                  </div>
                </div>

                {/* ===== ALL PRODUCTS GRID ===== */}
                <div className="px-4 pb-6" data-testid="all-products-grid">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)]">
                      All Products
                    </h3>
                  </div>
                  {loading ? (
                    <div className={PRODUCT_GRID}>
                      {[1,2,3,4].map(i => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />)}
                    </div>
                  ) : (
                    <div className={PRODUCT_GRID}>
                      {products.slice(0, 20).map(product => (
                        <ProductCardGrid key={product.id} product={product} currencySymbol={currencySymbol}
                          getItemQuantity={getItemQuantity} onAdd={(v) => handleAddToCart(product, v)} onAddBase={() => handleAddToCart(product)}
                          onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings}
                          autoPlay={autoPlay} detailEnabled={detailEnabled} navigate={navigate} />
                      ))}
                    </div>
                  )}
                  {products.length > 20 && (
                    <Link to="/products" className="block mt-4 lg:max-w-sm lg:mx-auto" data-testid="view-all-products-btn">
                      <Button className="w-full bg-[var(--theme-surface)] text-[var(--theme-primary)] border border-[var(--theme-primary)] hover:bg-[var(--theme-secondary)] rounded-xl">
                        View All Products
                      </Button>
                    </Link>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Reserves scroll space for the floating cart bar below (which is `fixed` and
          so doesn't occupy flow height on its own) — without this the last product
          card ends up partially hidden behind it. */}
      {totalItems > 0 && <div className="h-16 lg:hidden" aria-hidden="true" />}

      {/* ======= FLOATING CART BAR ======= */}
      {totalItems > 0 && (
        <div className="lg:hidden fixed left-0 right-0 z-40 px-4 pb-2" style={{ bottom: 'calc(68px + env(safe-area-inset-bottom, 0px))' }} data-testid="floating-cart">
          <div className="max-w-3xl mx-auto">
            <Link to="/cart" className="block">
              <div className="bg-[var(--theme-primary)] text-white rounded-xl px-4 py-3 flex items-center justify-between shadow-lg">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="font-semibold text-sm">{totalItems} item{totalItems > 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{currencySymbol}{totalAmount.toFixed(0)}</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* ======= BOTTOM NAVIGATION ======= */}
      <BottomNav />
    </div>
  );
};

/* ============================================= */
/*  Product Card - Carousel (Horizontal scroll)  */
/* ============================================= */
const ProductCardCarousel = ({ product, currencySymbol, onAdd, onAddBase, onUpdate, getItemQuantity, storeSettings, autoPlay, detailEnabled, navigate }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [pickerOpen, setPickerOpen] = useState(false);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);

  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;

  const quantity = hasVariants
    ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0)
    : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;

  return (
    <div className="instamart-product-card" data-testid={`carousel-product-${product.id}`}>
      {discount > 0 && (
        <div className="absolute top-1.5 left-1.5 z-10 bg-[var(--theme-primary)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
          {discount}% OFF
        </div>
      )}
      <div className="w-full relative mb-5">
        <div className={`w-full aspect-square bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center ${detailEnabled ? 'cursor-pointer' : ''}`}
          onClick={() => detailEnabled && navigate?.(`/product/${product.id}`)}>
          <ProductImageCarousel product={product} autoPlay={autoPlay} />
          {productOos && !comingSoon && <UnavailableBadge />}
        </div>
        {comingSoon ? <ComingSoonButton size="sm" className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-10 shadow-sm" testId={`coming-soon-carousel-${product.id}`} /> : !productOos && (quantity === 0 ? (
          <button onClick={() => hasVariants ? onAdd(activeVariant) : onAddBase?.()}
            disabled={hasVariants && activeVariant && isVariantUnavailable(activeVariant, storeSettings)}
            className="instamart-add-btn" data-testid={`add-carousel-${product.id}`}>
            ADD
          </button>
        ) : (
          <div className="instamart-qty-control">
            <button onClick={() => onUpdate(cartKey, quantity - 1)} data-testid={`minus-carousel-${product.id}`}>
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span>{quantity}</span>
            <button onClick={() => onUpdate(cartKey, quantity + 1)} data-testid={`plus-carousel-${product.id}`}>
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <h4 className="text-xs font-semibold text-[var(--theme-text-main)] line-clamp-2 leading-tight">{product.name}</h4>
      {product.description && <p className="text-[10px] text-[var(--theme-text-muted)] line-clamp-1 mt-0.5">{product.description}</p>}
      <div className="flex items-center gap-1 mt-0.5">
        <p className="text-[10px] text-[var(--theme-text-muted)]">{activeVariant ? activeVariant.label : product.unit}</p>
        {hasVariants && !productOos && !comingSoon && (
          <button onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
            className="flex items-center text-[9px] font-semibold text-[var(--theme-primary)]"
            data-testid={`change-variant-${product.id}`}>
            Change <ChevronDown className="w-2.5 h-2.5" />
          </button>
        )}
      </div>
      <VariantPicker open={pickerOpen} onOpenChange={setPickerOpen} productId={product.id} productName={product.name}
        variants={product.variants} selectedVariant={activeVariant} onSelect={setSelectedVariant}
        currencySymbol={currencySymbol} getItemQuantity={getItemQuantity} storeSettings={storeSettings} />
      <div className="flex items-center gap-1.5 mt-1">
        <span className="text-sm font-bold text-[var(--theme-text-main)]">{currencySymbol}{displayPrice}</span>
        {discount > 0 && <span className="text-[10px] text-[var(--theme-text-muted)] line-through">{currencySymbol}{displayMrp}</span>}
      </div>
    </div>
  );
};

/* ============================================= */
/*  Product Card - Grid (2 col for search/cat)   */
/* ============================================= */
const ProductCardGrid = ({ product, currencySymbol, onAdd, onAddBase, onUpdate, getItemQuantity, storeSettings, autoPlay, detailEnabled, navigate }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [pickerOpen, setPickerOpen] = useState(false);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);

  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;

  const quantity = hasVariants
    ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0)
    : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;

  return (
    <div className="bg-[var(--theme-surface)] rounded-xl p-3 border border-[var(--theme-border)] relative" data-testid={`grid-product-${product.id}`}>
      {discount > 0 && (
        <div className="absolute top-2 left-2 z-10 bg-[var(--theme-primary)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
          {discount}% OFF
        </div>
      )}
      <div className="relative mb-5">
        <div className={`aspect-square bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center ${detailEnabled ? 'cursor-pointer' : ''}`}
          onClick={() => detailEnabled && navigate?.(`/product/${product.id}`)}>
          <ProductImageCarousel product={product} autoPlay={autoPlay} />
          {productOos && !comingSoon && <UnavailableBadge />}
        </div>
        {comingSoon ? <ComingSoonButton size="sm" className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-10 shadow-sm" testId={`coming-soon-grid-${product.id}`} /> : !productOos && (quantity === 0 ? (
          <button onClick={() => hasVariants ? onAdd(activeVariant) : onAddBase?.()}
            disabled={hasVariants && activeVariant && isVariantUnavailable(activeVariant, storeSettings)}
            className="instamart-add-btn" data-testid={`add-grid-${product.id}`}>
            ADD
          </button>
        ) : (
          <div className="instamart-qty-control">
            <button onClick={() => onUpdate(cartKey, quantity - 1)} data-testid={`minus-grid-${product.id}`}>
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span>{quantity}</span>
            <button onClick={() => onUpdate(cartKey, quantity + 1)} data-testid={`plus-grid-${product.id}`}>
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <h4 className="text-xs font-semibold text-[var(--theme-text-main)] line-clamp-2 leading-tight">{product.name}</h4>
      {product.description && <p className="text-[10px] text-[var(--theme-text-muted)] line-clamp-1 mt-0.5">{product.description}</p>}
      <div className="flex items-center gap-1 mt-0.5">
        <p className="text-[10px] text-[var(--theme-text-muted)]">{activeVariant ? activeVariant.label : product.unit}</p>
        {hasVariants && !productOos && !comingSoon && (
          <button onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
            className="flex items-center text-[9px] font-semibold text-[var(--theme-primary)]"
            data-testid={`change-variant-${product.id}`}>
            Change <ChevronDown className="w-2.5 h-2.5" />
          </button>
        )}
      </div>
      <VariantPicker open={pickerOpen} onOpenChange={setPickerOpen} productId={product.id} productName={product.name}
        variants={product.variants} selectedVariant={activeVariant} onSelect={setSelectedVariant}
        currencySymbol={currencySymbol} getItemQuantity={getItemQuantity} storeSettings={storeSettings} />
      <div className="flex items-center gap-1.5 mt-1.5">
        <span className="text-sm font-bold text-[var(--theme-text-main)]">{currencySymbol}{displayPrice}</span>
        {discount > 0 && <span className="text-[10px] text-[var(--theme-text-muted)] line-through">{currencySymbol}{displayMrp}</span>}
      </div>
    </div>
  );
};

/* ============================= */
/*  Bottom Navigation Bar        */
/* ============================= */
const BottomNav = () => {
  const location = typeof window !== 'undefined' ? window.location.pathname : '/';
  const items = useCustomerNavItems();

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[var(--theme-surface)] border-t border-[var(--theme-border)] shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]" data-testid="instamart-bottom-nav"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="max-w-3xl mx-auto flex items-center justify-around py-2">
        {items.map(item => {
          const active = isNavItemActive(item.path, location);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 relative transition-colors ${active ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'}`}
              data-testid={`bnav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default InstamartHome;
